// PhaseChart.tsx
import React, { forwardRef, useRef, useEffect } from "react";
import ReactECharts from "echarts-for-react";
import { buildRetroChartOptions, RetroLimitKey } from "../options/buildRetroChartOptions";
import { LimitPoint, RetroChart as RetroChartModel } from "../../../models/chartData";

interface PhaseChartProps {
  title: string;
  chart: RetroChartModel;
  limits?: LimitPoint[];
  limitKey?: RetroLimitKey;
  colors?: Record<string, string>;
  activeSeries?: Record<string, boolean>;
  onToggleSeries?: (seriesName: string, selected?: boolean) => void;
  onDataZoom?: (params: any) => void;
  onCursorMove?: (time: number | null) => void;
  dynamicLevel: number;
  showLimit?: boolean;
  maxDate?: number;
  zoomStart?: number;
  zoomEnd?: number;
  zoomResetVersion?: number;
}

export const PhaseChart = forwardRef<any, PhaseChartProps>(
  (
    {
      title,
      chart,
      limits,
      limitKey,
      colors,
      activeSeries,
      onToggleSeries,
      onDataZoom,
      onCursorMove,
      dynamicLevel,
      showLimit = true,
      maxDate,
      zoomStart,
      zoomEnd,
      zoomResetVersion = 0,
    },
    ref,
  ) => {
    const chartRef = useRef<ReactECharts>(null);
    const prevVersionRef = useRef(zoomResetVersion);

    useEffect(() => {
      if (zoomResetVersion !== prevVersionRef.current) {
        const instance = chartRef.current?.getEchartsInstance();
        if (instance) {
          setTimeout(() => {
            instance.dispatchAction({ type: "dataZoom", start: 0, end: 100 });
          }, 0);
        }
        prevVersionRef.current = zoomResetVersion;
      }
    }, [zoomResetVersion, chart]);

    const options = buildRetroChartOptions({
      chart,
      limits: showLimit ? limits : [],
      limitKey: showLimit ? limitKey : undefined,
      activeSeries,
      colors,
      maxDate,
      zoomStart,
      zoomEnd,
      dynamicLevel,
    });

    const onEvents = {
      updateAxisPointer: (params: any) => {
        if (!onCursorMove) return;
        const xValue = params.axesInfo?.[0]?.value ?? null;
        onCursorMove(xValue);
      },
      dataZoom: onDataZoom,
      legendselectchanged: (params: any) => {
        const { name, selected } = params;
        if (onToggleSeries && selected[name] !== undefined) {
          onToggleSeries(name, selected[name]);
        }
      },
    };

    return (
      <div className="chart-section">
        <div className="chart-title">{title}</div>
        <ReactECharts
          ref={chartRef}
          option={options}
          style={{ height: 300, width: "100%" }}
          onEvents={onEvents}
          opts={{ renderer: "canvas" }}
          notMerge={false}
          lazyUpdate={true}
        />
      </div>
    );
  },
);

PhaseChart.displayName = "PhaseChart";
